// /////////////////////////////////////////////////////////////
// [ファイル] CustomerAssetList.tsx
// [概要] 顧客に紐づく添付ファイル (複数) の一覧・アップロード・削除。
//        メタデータは trn_customer_assets (API 経由)、実体は Firebase Storage
//        (crm-customers/{customerId}/{assetId}_{fileName}) へフロントから直接アップロードする。
//        手順は register (ID とパスを採番) → Storage へ put → confirm (サイズ確定) の 3 段階。
//        顧客詳細の表示モードから直接アップロード・ラベル変更・削除ができる (顧客本体の編集は不要)。
//        PDF・画像はモーダルダイアログ内で表示し、それ以外の形式は別タブへ委ねる。
//        新規登録中の顧客 (customerId 未確定) はアップロード不可。
// /////////////////////////////////////////////////////////////
import React, { useCallback, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase/firebase';
import * as Icons from '../constants/icons';
import { STANDARD_CTA_BUTTON_SX, STANDARD_TEXT_BUTTON_SX } from '../constants/buttonStyles';
import { useAuth } from '../firebase/AuthProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import { useNotification } from '../providers/NotificationProvider';
import { ep } from '../api/endpoints';

const REGISTER_URL = ep('register_crm_customer_asset');
const CONFIRM_URL = ep('confirm_crm_customer_asset');
const UPDATE_URL = ep('update_crm_customer_asset');
const DELETE_URL = ep('delete_crm_customer_asset');

// サーバー側 (customer_asset_service) と同じ制限
export const MAX_ASSET_FILE_BYTES = 20 * 1024 * 1024;
export const MAX_ASSETS_PER_CUSTOMER = 20;
const ACCEPT =
  '.pdf,image/*,.xlsx,.docx,.pptx,.xls,.doc,.ppt,.txt,.md,.csv,.tsv,.json,.xml,.html,.htm,.zip';
const ALLOWED_EXT_RE =
  /\.(pdf|jpg|jpeg|png|gif|webp|heic|bmp|tif|tiff|xlsx|docx|pptx|xls|doc|ppt|txt|md|csv|tsv|json|xml|html|htm|zip)$/i;

export interface CustomerAsset {
  id: number;
  file_name: string;
  label?: string | null;
  mime_type?: string | null;
  file_size?: number | null;
  gcs_path: string;
  source?: string;
  created_at?: string | null;
  created_by?: string | null;
}

interface Props {
  customerId?: string | null;
  assets: CustomerAsset[];
  // 追加・削除・ラベル更新の後に親へ再取得を促す
  onChanged?: () => void;
  // 指定時はプレビューに「ターコイズ詳細と並べる」ボタンを出す (ターコイズ紐付けのある顧客のみ)
  onCompareWithTurquoise?: (asset: CustomerAsset) => void;
}

export const formatBytes = (n?: number | null): string => {
  if (!n) return '';
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
};

// ダイアログ内で直接表示できる種類 (それ以外は別タブへ委ねる)
export const previewKindOf = (asset: CustomerAsset): 'pdf' | 'image' | 'other' => {
  const n = (asset.file_name || '').toLowerCase();
  const mime = (asset.mime_type || '').toLowerCase();
  if (mime === 'application/pdf' || n.endsWith('.pdf')) return 'pdf';
  if (mime.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|bmp)$/.test(n)) return 'image';
  return 'other';
};

export const iconFor = (fileName: string) => {
  const n = fileName.toLowerCase();
  if (n.endsWith('.pdf')) return <Icons.PictureAsPdf sx={{ fontSize: 18, color: '#c62828' }} />;
  if (/\.(jpg|jpeg|png|gif|webp|heic|bmp|tif|tiff)$/.test(n)) return <Icons.Image sx={{ fontSize: 18, color: '#2e7d32' }} />;
  return <Icons.InsertDriveFile sx={{ fontSize: 18, color: '#616161' }} />;
};

const CustomerAssetList: React.FC<Props> = ({ customerId, assets, onChanged, onCompareWithTurquoise }) => {
  const { user } = useAuth();
  const confirm = useConfirm();
  const notify = useNotification();
  const [uploading, setUploading] = useState(false);
  // ラベル変更ダイアログ
  const [labelTarget, setLabelTarget] = useState<CustomerAsset | null>(null);
  const [labelDraft, setLabelDraft] = useState('');
  // プレビューダイアログ (url は取得完了まで null)
  const [preview, setPreview] = useState<{ asset: CustomerAsset; url: string | null; loading: boolean } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ダイアログを開いたら現在のラベルを下書きへ写す
  React.useEffect(() => {
    if (labelTarget) setLabelDraft(labelTarget.label || '');
  }, [labelTarget]);

  const list = Array.isArray(assets) ? assets : [];

  // PDF・画像はダイアログ内で直接表示する。それ以外はブラウザに委ねて別タブで開く
  const handleOpen = useCallback(
    async (asset: CustomerAsset) => {
      const kind = previewKindOf(asset);
      try {
        if (kind === 'other') {
          const url = await getDownloadURL(ref(storage, asset.gcs_path));
          window.open(url, '_blank', 'noopener');
          return;
        }
        setPreview({ asset, url: null, loading: true });
        const url = await getDownloadURL(ref(storage, asset.gcs_path));
        // 取得中に閉じられた / 別ファイルへ切り替えられた場合は破棄する
        setPreview((cur) => (cur && cur.asset.id === asset.id ? { ...cur, url, loading: false } : cur));
      } catch (e) {
        console.error('添付の取得エラー:', e);
        setPreview(null);
        notify.error('ファイルを開けませんでした');
      }
    },
    [notify],
  );

  // プレビュー中のファイルを別タブで開く (拡大表示・印刷用)
  const openPreviewInNewTab = useCallback(() => {
    if (preview?.url) window.open(preview.url, '_blank', 'noopener');
  }, [preview]);

  // プレビュー対象を前後のファイルへ送る
  const stepPreview = useCallback(
    (delta: number) => {
      if (!preview) return;
      const idx = list.findIndex((x) => x.id === preview.asset.id);
      const next = list[idx + delta];
      if (next) handleOpen(next);
    },
    [preview, list, handleOpen],
  );

  const handleUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      e.target.value = '';
      if (!files.length || !customerId) return;
      if (list.length + files.length > MAX_ASSETS_PER_CUSTOMER) {
        notify.error(`添付は 1 顧客につき ${MAX_ASSETS_PER_CUSTOMER} 件までです`);
        return;
      }
      setUploading(true);
      let ok = 0;
      try {
        for (const file of files) {
          if (!ALLOWED_EXT_RE.test(file.name)) {
            notify.warning(`「${file.name}」は添付できない形式です`);
            continue;
          }
          if (file.size > MAX_ASSET_FILE_BYTES) {
            notify.warning(`「${file.name}」はサイズ上限 (${MAX_ASSET_FILE_BYTES / 1024 / 1024} MB) を超えています`);
            continue;
          }
          try {
            // 1. メタデータ登録 (ID とアップロード先パスを採番)
            const reg = await axios.post(REGISTER_URL, {
              customerId,
              fileName: file.name,
              mimeType: file.type || null,
              fileSize: file.size,
              operator_id: user?.email,
            });
            if (reg.data.status !== 'success') {
              notify.error(reg.data.message || `「${file.name}」の登録に失敗しました`);
              continue;
            }
            const { asset_id: assetId, gcs_path: gcsPath } = reg.data.result;
            // 2. Storage へ実体をアップロード
            await uploadBytes(ref(storage, gcsPath), file, {
              contentType: file.type || 'application/octet-stream',
            });
            // 3. サイズ確定
            await axios.post(CONFIRM_URL, { assetId, fileSize: file.size, operator_id: user?.email });
            ok += 1;
          } catch (err: any) {
            console.error('添付アップロードエラー:', err);
            notify.error(err?.response?.data?.message || `「${file.name}」のアップロードに失敗しました`);
          }
        }
        if (ok > 0) {
          notify.success(`${ok} 件のファイルを添付しました`);
          onChanged?.();
        }
      } finally {
        setUploading(false);
      }
    },
    [customerId, list.length, user, notify, onChanged],
  );

  const handleDelete = useCallback(
    async (asset: CustomerAsset) => {
      const ok = await confirm({
        title: '添付ファイルの削除',
        message: `「${asset.file_name}」を削除しますか？\nこの操作は取り消せません。`,
        confirmText: '削除する',
        destructive: true,
      });
      if (!ok) return;
      try {
        const res = await axios.post(DELETE_URL, { assetId: asset.id, operator_id: user?.email });
        if (res.data.status === 'success') {
          notify.success('添付を削除しました');
          onChanged?.();
        } else {
          notify.error(res.data.message || '削除に失敗しました');
        }
      } catch (err: any) {
        console.error('添付削除エラー:', err);
        notify.error(err?.response?.data?.message || '削除に失敗しました');
      }
    },
    [confirm, user, notify, onChanged],
  );

  // ラベル変更ダイアログから保存する
  const saveLabelDialog = useCallback(async () => {
    if (!labelTarget) return;
    const target = labelTarget;
    setLabelTarget(null);
    if (labelDraft === (target.label || '')) return;
    try {
      const res = await axios.post(UPDATE_URL, { assetId: target.id, label: labelDraft, operator_id: user?.email });
      if (res.data.status === 'success') {
        notify.success('ラベルを更新しました');
        onChanged?.();
      } else {
        notify.error(res.data.message || 'ラベルの更新に失敗しました');
      }
    } catch (err: any) {
      console.error('ラベル更新エラー:', err);
      notify.error('ラベルの更新に失敗しました');
    }
  }, [labelTarget, labelDraft, user, notify, onChanged]);

  // ============================
  // 一覧 + アップロード (顧客詳細から直接操作でき、顧客本体の編集モードには入らない)
  // ============================
  return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.25 }}>
        {list.length === 0 && (
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>-</Typography>
        )}
        {list.map((a) => (
          <Box key={a.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {iconFor(a.file_name)}
            <Link
              component="button"
              type="button"
              onClick={() => handleOpen(a)}
              sx={{ fontSize: '0.875rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, cursor: 'pointer', wordBreak: 'break-all', textAlign: 'left' }}
            >
              {a.file_name}
            </Link>
            {a.label && (
              <Chip label={a.label} size="small" sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }} />
            )}
            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
              {formatBytes(a.file_size)}
            </Typography>
            <Tooltip title="ラベルを付ける・変更する">
              <IconButton size="small" onClick={() => setLabelTarget(a)} sx={{ p: 0.25 }}>
                <Icons.Edit sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="削除">
              <IconButton size="small" onClick={() => handleDelete(a)} sx={{ p: 0.25 }}>
                <Icons.Delete sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ))}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
          <input ref={fileInputRef} type="file" accept={ACCEPT} multiple hidden onChange={handleUpload} />
          <Button
            size="small"
            startIcon={uploading ? <CircularProgress size={14} /> : <Icons.AttachFile />}
            onClick={() => fileInputRef.current?.click()}
            disabled={!customerId || uploading || list.length >= MAX_ASSETS_PER_CUSTOMER}
            sx={STANDARD_TEXT_BUTTON_SX}
          >
            {uploading ? 'アップロード中...' : 'ファイルを添付'}
          </Button>
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
            {list.length} / {MAX_ASSETS_PER_CUSTOMER} 件 (1 ファイル {MAX_ASSET_FILE_BYTES / 1024 / 1024} MB まで)
          </Typography>
        </Box>
        {/* ラベル編集ダイアログ (表示モードでも付け替えられるように) */}
        {/* 添付のプレビュー (PDF・画像) */}
        <Dialog
          open={preview !== null}
          onClose={() => setPreview(null)}
          maxWidth="lg"
          fullWidth
          disableScrollLock
          PaperProps={{ sx: { height: '90vh' } }}
        >
          <DialogTitle
            sx={{
              fontSize: '0.875rem', fontWeight: 700, py: 0.75,
              display: 'flex', alignItems: 'center', gap: 1,
            }}
          >
            {preview && iconFor(preview.asset.file_name)}
            <Box component="span" sx={{ wordBreak: 'break-all' }}>{preview?.asset.file_name}</Box>
            {preview?.asset.label && (
              <Chip label={preview.asset.label} size="small" sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }} />
            )}
            <Box sx={{ flex: 1 }} />
            {list.length > 1 && preview && (
              <>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
                  {list.findIndex((x) => x.id === preview.asset.id) + 1} / {list.length}
                </Typography>
                <Tooltip title="前のファイル">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => stepPreview(-1)}
                      disabled={list.findIndex((x) => x.id === preview.asset.id) <= 0}
                      sx={{ p: 0.25 }}
                    >
                      <Icons.ChevronLeft sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="次のファイル">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => stepPreview(1)}
                      disabled={list.findIndex((x) => x.id === preview.asset.id) >= list.length - 1}
                      sx={{ p: 0.25 }}
                    >
                      <Icons.ChevronRight sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
            {onCompareWithTurquoise && preview && (
              <Button
                size="small"
                startIcon={<Icons.VerticalSplit sx={{ fontSize: 16 }} />}
                onClick={() => {
                  // プレビューを閉じ、ターコイズ詳細 (左) とこのファイル (右) の横並び表示へ切り替える
                  const target = preview.asset;
                  setPreview(null);
                  onCompareWithTurquoise(target);
                }}
                sx={{ ...STANDARD_TEXT_BUTTON_SX, fontSize: '0.75rem', whiteSpace: 'nowrap' }}
              >
                ターコイズ詳細と並べる
              </Button>
            )}
            <Tooltip title="別タブで開く">
              <span>
                <IconButton size="small" onClick={openPreviewInNewTab} disabled={!preview?.url} sx={{ p: 0.25 }}>
                  <Icons.OpenInNew sx={{ fontSize: 17 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="閉じる">
              <IconButton size="small" onClick={() => setPreview(null)} sx={{ p: 0.25 }}>
                <Icons.Close sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0, bgcolor: '#525659', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {preview?.loading && <CircularProgress size={32} sx={{ color: '#fff' }} />}
            {preview && !preview.loading && preview.url && (
              previewKindOf(preview.asset) === 'pdf' ? (
                <Box
                  component="iframe"
                  src={preview.url}
                  title={preview.asset.file_name}
                  sx={{ width: '100%', height: '100%', border: 0 }}
                />
              ) : (
                <Box
                  component="img"
                  src={preview.url}
                  alt={preview.asset.file_name}
                  sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              )
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem', mr: 'auto' }}>
              {formatBytes(preview?.asset.file_size)}
            </Typography>
            <Button onClick={() => setPreview(null)} sx={STANDARD_TEXT_BUTTON_SX}>閉じる</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={labelTarget !== null} onClose={() => setLabelTarget(null)} maxWidth="xs" fullWidth disableScrollLock>
          <DialogTitle sx={{ fontSize: '0.875rem', fontWeight: 700, py: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icons.Edit sx={{ fontSize: 18 }} /> ラベルの変更
          </DialogTitle>
          <DialogContent sx={{ pt: '12px !important' }}>
            <Typography variant="body2" sx={{ mb: 1, wordBreak: 'break-all' }}>{labelTarget?.file_name}</Typography>
            <TextField
              fullWidth
              size="small"
              autoFocus
              placeholder="例: 登録申請書, 押印契約書"
              value={labelDraft}
              onChange={(e) => setLabelDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  saveLabelDialog();
                }
              }}
              inputProps={{ maxLength: 100 }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setLabelTarget(null)} sx={STANDARD_TEXT_BUTTON_SX}>キャンセル</Button>
            <Button onClick={saveLabelDialog} startIcon={<Icons.Save />} sx={STANDARD_CTA_BUTTON_SX}>保存する</Button>
          </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerAssetList;
